import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, Trash2 } from 'lucide-react';
import SessionCard from './SessionCard';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  instructor_id: string | null;
  created_at: string;
  attendance_count?: number;
  age_group?: 'all' | 'kids' | 'teens' | 'adults';
  status?: 'scheduled' | 'completed' | 'cancelled';
}

interface PromotionSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  created_by: string | null;
  created_at: string;
  age_group?: 'all' | 'kids' | 'adults';
}

interface SessionListProps {
  sessions: TrainingSession[];
  promotionSessions: PromotionSession[];
  userRole: string | undefined;
  onEdit: (session: TrainingSession) => void;
  onDelete: (sessionId: string) => void;
  onNotify: (session: TrainingSession) => void;
  onConfirmAttendance: (session: TrainingSession) => void;
  onConfirmPromotionAttendance: (sessionId: string, willAttend: boolean) => void;
}

const SessionList: React.FC<SessionListProps> = ({
  sessions,
  promotionSessions,
  userRole,
  onEdit,
  onDelete,
  onNotify,
  onConfirmAttendance,
  onConfirmPromotionAttendance
}) => {
  useUiLanguage();
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLocale(), {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const groupAllSessionsByDate = (trainingSessions: TrainingSession[], promotionSessions: PromotionSession[]) => {
    const allSessions = [
      ...trainingSessions.map(session => ({ ...session, type: 'training' as const })),
      ...promotionSessions.map(session => ({ ...session, type: 'promotion' as const }))
    ];

    const grouped = allSessions.reduce((acc, session) => {
      const date = session.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(session);
      return acc;
    }, {} as Record<string, any[]>);

    return Object.entries(grouped).sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.length === 0) {
      toast({
        title: uiText("No Sessions Selected"),
        description: uiText("Please select sessions to delete."),
        variant: "destructive"
      });
      return;
    }

    if (!window.confirm(uiText("Are you sure you want to delete {{value0}} session(s)? This action cannot be undone.", { value0: selectedSessions.length }))) {
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('training_sessions')
        .delete()
        .in('id', selectedSessions);

      if (error) throw error;

      setSelectedSessions([]);
      toast({
        title: uiText("Sessions Deleted"),
        description: uiText("Successfully deleted {{value0}} session(s).", { value0: selectedSessions.length })
      });

      // Refresh the sessions list
      window.location.reload();
    } catch (error) {
      console.error('Error deleting sessions:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to delete sessions."),
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSessionSelect = (sessionId: string, checked: boolean) => {
    if (checked) {
      setSelectedSessions(prev => [...prev, sessionId]);
    } else {
      setSelectedSessions(prev => prev.filter(id => id !== sessionId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allSessionIds = sessions.map(s => s.id);
      setSelectedSessions(allSessionIds);
    } else {
      setSelectedSessions([]);
    }
  };

  const groupedSessions = groupAllSessionsByDate(sessions, promotionSessions);

  if (groupedSessions.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{uiText("No Training Sessions Scheduled")}</h3>
          <p className="text-gray-500 mb-6">
            {uiText("Training sessions will appear here once they are scheduled.")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {(userRole === 'admin' || userRole === 'super_user') && sessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{uiText("Bulk Actions")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedSessions.length === sessions.length && sessions.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                  <label htmlFor="select-all" className="text-sm font-medium">
                    {uiText("Select All (")}{selectedSessions.length} {' '}{uiText("selected)")}</label>
                </div>
              </div>
              <Button
                onClick={handleBulkDelete}
                disabled={selectedSessions.length === 0 || isDeleting}
                variant="destructive"
                size="sm"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {isDeleting ? uiText("Deleting...") : uiText("Delete Selected ({{value0}})", { value0: selectedSessions.length })}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {groupedSessions.map(([date, dateSessions]) => (
        <Card key={date}>
          <CardHeader>
            <CardTitle className="text-lg text-red-900">
              {formatDate(date)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dateSessions.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  userRole={userRole}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onNotify={onNotify}
                  onConfirmAttendance={onConfirmAttendance}
                  onConfirmPromotionAttendance={onConfirmPromotionAttendance}
                  isSelected={selectedSessions.includes(session.id)}
                  onSelect={handleSessionSelect}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SessionList;
